import React, { Key, useEffect, useRef, useState } from 'react';
import {
  FieldProps,
  wdiCore,
  WdiInput,
  WdiModalAppProps,
  WdiModalAppResult,
  WdiModalProps,
} from '@/components/WdiControls';
import { WdiModal } from '@/components/WdiControls/WdiModalForm';
import { ButtonColumn, FilterColumn, GridFilterForm } from '@/components/GridWidget/GridFilterForm';
import { Button, Input, message } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { SelectParseModalApp, WdiSelect, WdiTreeSelect } from '@/components/WdiControls/WdiSelect';
import { SorterResult } from 'antd/lib/table/interface';
import { WdiTable } from '@/components/WdiControls/WdiTable';
import { ResponseStatus } from '@/utils/request';
import { toExcel } from '@/components/WdiControls/WdiExcel';
import { SoldToReal } from '@/services/mdm/soldtoreal';
import { BlockOutlined, CloseOutlined } from '@ant-design/icons';
import { BO } from '@/services/mdm/bo';
import { CustType } from '@/services/mdm/custtype';
import { CustSys } from '@/services/mdm/custsys';
import { Enum } from '@/enums/system';
import { soldToRealLoadingResultFormat } from '@/pages/sales/sa/components/data';
import SelectedApp from '@/pages/sales/sa/PriceDiscountApply/components/SelectedApp';

export type SoldToRealSelectAppProps = {
  value?: SoldToRealValue;
  onChange?: (value?: SoldToRealValue) => void;
  data?: any;
};

type WdiSelectComponentStates = {
  label: any;
  value: any;
};

const validate = (data: any) => {
  const {boIds, custTypeId} = data;
  if (!boIds || !custTypeId) {
    message.error('请先选择使用部门/客户分类').then();
    return false;
  }
  return true;
};

const SoldToRealSelectApp: React.FC<SoldToRealSelectAppProps> = (props) => {
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false});
  const [pareModalProps, setPareModalProps] = useState<WdiModalProps>({visible: false});
  const [dataSource, setDataSource] = useState<WdiSelectComponentStates[]>();
  const onClose = () => {
    setModalProps({visible: false, title: '', id: null, data: null});
  };
  const showParseModal = () => {
    if (!validate(props.data)) {
      return;
    }
    setPareModalProps({visible: true, title: '复制粘贴'});
  };

  const onSave = (result?: WdiModalAppResult) => {
    setModalProps({visible: false, title: '', id: null, data: null});
    if (result && result.code === ResponseStatus.SUCCESS && props.onChange) {
      const codes: string[] = [];
      const options: any[] = [];
      if (result.data?.codes) {
        codes.push(...result.data?.codes.split(',').filter((m: any) => !codes.includes(m)));
        options.push(
          ...result.data?.options.filter(
            (m: any) => options.findIndex((n: any) => m.value == n.value) < 0,
          ),
        );
      }
      props.onChange({
        codes: codes.join(),
        options: options,
      });
    }
  };

  useEffect(() => {
    if (props.value) {
      setDataSource(props.value?.options);
    } else {
      setDataSource([]);
    }
  }, [props.value]);

  const newProps: any = {
    onClick: () => {
      if (!validate(props.data)) {
        return;
      }
      setModalProps({
        visible: true,
        title: '选择ERP客户',
        id: null,
        data: props.data,
      });
    },
    allowClear: {
      clearIcon: (
        <Button
          shape="circle"
          style={{border: 'none', width: 20, height: 20}}
          icon={<CloseOutlined/>}
          size="small"
          onClick={() => {
            props.onChange?.(undefined);
          }}
        />
      ),
    },
  };
  return (
    <div style={{display: 'flex', alignItems: 'center'}}>
      <WdiInput
        style={{color: '#1296db', cursor: 'pointer'}}
        allowClear={true}
        {...newProps}
        placeholder="请选择ERP客户"
        value={props.value?.options.map((item: any) => item.label).join()}
      />
      <BlockOutlined onClick={() => showParseModal()} style={{fontSize: 20, marginLeft: 8}}/>
      {modalProps.visible && (
        <SoldToRealSelectModalApp {...modalProps} onClose={onClose} onSave={onSave}/>
      )}
      {pareModalProps.visible && (
        <SelectParseModalApp
          onClose={() => setPareModalProps({visible: false})}
          onSave={(result) => {
            setPareModalProps({visible: false});
            if (result && result.code === ResponseStatus.SUCCESS && props.onChange) {
              // 去重 codes
              const codeSet = new Set();
              const uniqueCodes = result.data
                ?.map((m: any) => m.value)
                .filter((value: any) => {
                  if (!codeSet.has(value)) {
                    codeSet.add(value);
                    return true;
                  }
                  return false;
                });

              // 去重 options
              const optionMap = new Map();
              const uniqueOptions = result.data?.filter((option: any) => {
                if (!optionMap.has(option.value)) {
                  optionMap.set(option.value, option);
                  return true;
                }
                return false;
              });

              props.onChange({
                codes: uniqueCodes?.join(),
                options: uniqueOptions,
              });
            }
          }}
          dataLoading={(params: any) => {
            const promise = SoldToReal.search(
              Object.assign(
                {
                  withPermissions: true,
                  isActive: true,
                  ...props.data,
                  soldToRealCodes: undefined,
                },
                params,
              ),
            );
            return soldToRealLoadingResultFormat(promise);
          }}
          optionFilter={(item: any, options: any[]) =>
            options.find(
              (option) =>
                wdiCore.String.EqIngoreCase(option.code, item) ||
                wdiCore.String.EqIngoreCase(option.name, item) ||
                wdiCore.String.EqIngoreCase(option.label, item),
            )
          }
          dataSource={dataSource}
          {...pareModalProps}
        />
      )}
    </div>
  );
};

type TableParams = {
  pagination: TablePaginationConfig;
  sorter?: SorterResult<any> | null | undefined;
  filterOptions: any;
};

const columns: ColumnsType<any> = [
  {
    title: 'ERP客户编号',
    width: 120,
    dataIndex: 'soldToRealCode',
    ellipsis: true,
  },
  {
    title: 'ERP客户名称',
    width: 120,
    dataIndex: 'soldToRealName',
    ellipsis: true,
  },
  {
    title: '客户组',
    width: 180,
    dataIndex: 'custSysCode',
    ellipsis: true,
    render: (_, record) => wdiCore.String.CodeOrName(record.custSysCode, record.custSysName),
  },
  {
    title: '客户一级分类名称',
    width: 120,
    dataIndex: 'custTypeName1',
    ellipsis: true,
  },
  {
    title: '客户二级分类名称',
    width: 120,
    dataIndex: 'custTypeName2',
    ellipsis: true,
  },
  {
    title: '客户三级分类名称',
    dataIndex: 'custTypeName3',
    width: 120,
    ellipsis: true,
  },
];

type SoldToRealValue = {
  codes: string;
  options: { value: string; label: string }[];
};

const SoldToRealSelectModalApp: React.FC<WdiModalAppProps> = (props) => {
  const {data} = props;

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      defaultPageSize: 10,
      total: 0,
      showTotal: (total) => {
        return `共 ${total} 条`;
      },
      showQuickJumper: false,
    },
    sorter: null,
    filterOptions: null,
  });
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [allSelectedRowKeys, setAllSelectedRowKeys] = useState<Key[]>([]);
  const [allSelectedRows, setAllSelectedRows] = useState<any[]>([]);
  const currentPageDataRef = useRef<any[]>([]);

  const buttonColumns: ButtonColumn[] = [
    {
      title: '导出',
      key: 'Export',
      doClick: (filterOptions: any) => {
        let columns: any[] = [
          {name: 'soldToRealCode', displayName: 'ERP客户编号'},
          {name: 'soldToRealName', displayName: 'ERP客户名称'},
          {name: 'custSysCode', displayName: '客户组'},
          {name: 'custTypeName1', displayName: '客户一级分类名称'},
          {name: 'custTypeName2', displayName: '客户二级分类名称'},
          {name: 'custTypeName3', displayName: '客户三级分类名称'},
        ];
        const req: API.QueryForPageReq = {
          filterOptions: JSON.stringify(
            getFilterOptions(Object.assign({}, tableParams, {filterOptions})),
          ),
          pagenum: 0,
          pagesize: 0,
        };
        SoldToReal.queryForSelect(req).then((response) => {
          const sheet = {
            data: response?.data?.records,
            names: columns,
            sheetName: 'ERP客户',
            cells: {
              custSysCode: (fieldObject: any, row: any, value: any) => {
                return wdiCore.String.CodeOrName(row.custSysCode, row.custSysName);
              },
            },
          };
          toExcel({
            name: 'ERP客户_' + wdiCore.Date.ToString(new Date()) + '.xlsx',
            sheetProps: [sheet],
          });
        });
      },
    },
  ];
  const filterColumns: FilterColumn[] = [
    {
      title: 'ERP客户编号',
      dataIndex: 'soldToRealCode',
      span: 4,
      render: () => {
        return <Input/>;
      },
    },
    {
      title: 'ERP客户名称',
      dataIndex: 'soldToRealName',
      span: 4,
      render: () => {
        return <Input/>;
      },
    },
    {
      title: '使用部门',
      dataIndex: 'boId',
      span: 4,
      render: () => {
        return (
          <WdiSelect
            {...{
              remote: true,
              disabled: true,
              defaultValue: data.boIds.split(',').map(Number),
              mode: 'multiple',
              reloadKey: data.boIds,
              dataLoading: async (params) => {
                const response = await BO.search(Object.assign({boIds: data.boIds}, params));
                return response.data.map((item) => {
                  return {value: item.boId, label: item.boName};
                });
              },
            }}
          />
        );
      },
    },
    {
      title: '客户分类',
      dataIndex: 'custTypeId',
      span: 4,
      render: () => {
        return (
          <WdiTreeSelect
            {...{
              disabled: true,
              defaultValue: data.custTypeId,
              dataLoading: async () => {
                const response = await CustType.search({
                  withPermissions: true,
                  isActive: true,
                  custTypeLevels: [
                    Enum.CustTypeLevel.LEVEL1,
                    Enum.CustTypeLevel.LEVEL2,
                    Enum.CustTypeLevel.LEVEL3,
                  ].join(),
                });
                const {treeData, dict} = FieldProps.TreeSelect.formatTreeData(
                  {
                    textField: 'custTypeName',
                    valueField: 'custTypeId',
                    id: 'custTypeId',
                    pId: 'parentId',
                    level: 'custTypeLevel',
                  },
                  response.data,
                );
                return {treeData, dict};
              },
            }}
          />
        );
      },
    },
    {
      title: '客户组',
      dataIndex: 'custSysCode',
      span: 4,
      render: () => {
        return (
          <WdiSelect
            {...{
              placeholder: '请选择客户组',
              showSearch: true,
              remote: true,
              disabled: data?.custSysCode,
              defaultValue: data?.custSysCode ? data.custSysCode : undefined,
              mode: 'multiple',
              dataLoading: async (params) => {
                const response = await CustSys.search(
                  Object.assign(
                    {
                      withPermissions: true,
                      isActive: true,
                    },
                    params,
                  ),
                );
                return response.data.map((item) => {
                  return {value: item.custSysCode, label: item.codeOrName};
                });
              },
            }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    fetchData(Object.assign({}, tableParams));
  }, []);

  useEffect(() => {
    if (data && data.soldToRealCodes) {
      const initialSelectedKeys = data.soldToRealCodes.split(',');
      setAllSelectedRowKeys(initialSelectedKeys);
      setAllSelectedRows([]);
    }
  }, [data?.soldToRealCodes]);

  const fetchFullDataForSelection = async (keys: any[]) => {
    if (keys.length === 0) return;
    const response = await SoldToReal.search({ soldToRealCodes: keys.join(',') });
    if (response.data) {
      setAllSelectedRows(response.data);
    }
  };

  const getFilterOptions = (params: TableParams) => {
    const filterOptions: any = {
      boIds: data.boIds ? data.boIds.split(',').map(Number) : undefined,
      custTypeId: data.custTypeId ? data.custTypeId : undefined,
      custSysCode: data.custSysCode ? data.custSysCode.split(',') : undefined,
      soldToRealCodes: data.filterSoldToRealCodes ? data.filterSoldToRealCodes : undefined,
    };

    if (params.filterOptions) {
      const {soldToRealCode, soldToRealName, boIds, custTypeId, custSysCode} =
        params.filterOptions;
      if (!wdiCore.Object.IsNullOrUndefined(soldToRealCode) && soldToRealCode.length > 0) {
        filterOptions.soldToRealCode = soldToRealCode;
      }
      if (!wdiCore.Object.IsNullOrUndefined(soldToRealName) && soldToRealName.length > 0) {
        filterOptions.soldToRealName = soldToRealName;
      }
      if (!wdiCore.Object.IsNullOrUndefined(boIds) && boIds.length > 0) {
        filterOptions.boIds = boIds.join();
      }
      if (!wdiCore.Object.IsNullOrUndefined(custTypeId) && custTypeId.length > 0) {
        filterOptions.custTypeId = custTypeId;
      }
      if (!wdiCore.Object.IsNullOrUndefined(custSysCode) && custSysCode.length > 0) {
        filterOptions.custSysCode = custSysCode;
      }
    }
    return filterOptions;
  };
  const fetchData = (params: TableParams) => {
    setLoading(true);
    const filterOptions = getFilterOptions(params);
    const req: API.QueryForPageReq = {
      filterOptions: JSON.stringify(filterOptions),
      pagenum: params.pagination.current ? params.pagination.current - 1 : 0,
      pagesize: params.pagination.pageSize ? params.pagination.pageSize : 10,
    };

    SoldToReal.queryForSelect(req).then((responseResult) => {
      const data = responseResult.data;
      if (data) {
        currentPageDataRef.current = data.records;
        const currentPageSelectedRows = data.records.filter((item) =>
          allSelectedRowKeys.includes(item.soldToRealCode),
        );
        const mergedSelectedRows = [
          ...allSelectedRows.filter(
            (row) => !data.records.some((item) => item.soldToRealCode === row.soldToRealCode),
          ),
          ...currentPageSelectedRows,
        ];
        setDataSource(data.records);
        setTableParams({
          pagination: {
            ...params.pagination,
            total: data.totalCount,
          },
          sorter: params.sorter,
          filterOptions: params.filterOptions,
        });
        if (currentPageSelectedRows.length > 0) {
          setAllSelectedRows(mergedSelectedRows);
        }
      }
      setLoading(false);
    });
  };
  useEffect(() => {
    if (allSelectedRowKeys.length > 0 && allSelectedRows.length === 0) {
      fetchFullDataForSelection(allSelectedRowKeys);
    }
  }, [allSelectedRowKeys]);

  const getRowSelection = () => {
    const currentPageSelectedKeys = currentPageDataRef.current
      .filter((item) => allSelectedRowKeys.includes(item.soldToRealCode))
      .map((item) => item.soldToRealCode);

    return {
      selectedRowKeys: currentPageSelectedKeys,
      onChange: onSelectChange,
    };
  };

  const doSearch = (filterOptions: any) => {
    fetchData(Object.assign({}, tableParams, {filterOptions}));
  };

  const onSelectChange = (newSelectedRowKeys: Key[], newSelectedRows: any[]) => {
    const currentPageMap = new Map(
      currentPageDataRef.current.map((item) => [item.soldToRealCode, item]),
    );
    const newlySelectedKeys = newSelectedRowKeys.filter(
      (key) => !allSelectedRowKeys.includes(key) && currentPageMap.has(key as string),
    );
    const previouslySelectedKeys = allSelectedRowKeys.filter(
      (key) => !newSelectedRowKeys.includes(key) && currentPageMap.has(key as string),
    );
    const updatedAllSelectedRows = [
      ...allSelectedRows.filter((row) => !previouslySelectedKeys.includes(row.soldToRealCode)),
      ...newSelectedRows,
    ];
    const uniqueSelectedRows = updatedAllSelectedRows.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.soldToRealCode === item.soldToRealCode),
    );
    setAllSelectedRowKeys([
      ...allSelectedRowKeys.filter((key) => !previouslySelectedKeys.includes(key)),
      ...newlySelectedKeys,
    ]);
    setAllSelectedRows(uniqueSelectedRows);
  };
  const doSave = () => {
    if (allSelectedRowKeys) {
      const data = {
        codes: allSelectedRowKeys.join(),
        codeOrNames: allSelectedRows.map((item) => item.codeOrName).join(),
        options: allSelectedRows.map((item) => ({
          value: item.soldToRealCode,
          label: item.codeOrName,
        })),
      };
      if (props.onSave) props.onSave({ code: ResponseStatus.SUCCESS, data: data });
    }
    return true;
  };
  //删除已选客户
  const handleRemoveSelected = (key: Key) => {
    const newSelectedRows = allSelectedRows.filter((item) => item.soldToRealCode !== key);
    const newSelectedKeys = allSelectedRowKeys.filter((k) => k !== key);

    setAllSelectedRows(newSelectedRows);
    setAllSelectedRowKeys(newSelectedKeys);
  };

  return (
    <WdiModal
      title={props.title}
      visible={props.visible}
      width={1200}
      onCancel={() => {
        props.onClose ? props.onClose() : null;
      }}
      onOk={() => {
        if (allSelectedRows.length == 0) {
          message.error('请至少选择一条数据');
        } else {
          doSave();
        }
      }}
    >
      <SelectedApp data={allSelectedRows} onDelete={handleRemoveSelected} />
      <GridFilterForm doSearch={doSearch} columns={filterColumns} buttonColumns={buttonColumns} />
      <WdiTable
        showPageRows={10}
        rowKey="soldToRealCode"
        rowSelection={getRowSelection()}
        columns={columns}
        size="small"
        dataSource={dataSource}
        pageSizeOptions={[10, 50, 100, 500]}
        pagination={{
          ...tableParams.pagination,
          onChange: (page, pageSize) => {
            fetchData(
              Object.assign({}, tableParams, {
                pagination: {
                  ...tableParams.pagination,
                  current: page,
                  pageSize: pageSize,
                },
              }),
            );
          },
        }}
        loading={loading}
      />
    </WdiModal>
  );
};
export default SoldToRealSelectApp;
